import type { ReactNode } from "react";

import type { SettingsResponse } from "@/api/client";
import Panel from "@/components/common/Panel";
import { cn } from "@/lib/utils";
import { COMPILED_DEFAULTS, compiledOf } from "@/lib/compiledDefaults";

/**
 * Le rail « Effectif » — chaque valeur read-only que le moteur exécute, en UNE carte
 * dense et sticky. Valeurs en mono avec unité ; toute valeur qui diverge du défaut
 * compilé porte un chip ambre « ≠ défaut » (le drift ConfigMap/override se voit d'un
 * coup d'œil). Les constantes SPEC non éditables (daily loss ×, scaling review %) ont
 * été retirées — pas de clé de drift, aucune décision associée.
 */
export default function EffectiveRail({
  settings,
  className,
}: {
  settings: SettingsResponse;
  className?: string;
}) {
  const s = settings;
  const pct = (v: number) => `${(v * 100).toFixed(0)}%`;

  return (
    <div className={cn("flex flex-col gap-4", className)} data-testid="effective-rail">
      <Panel
        title="Effectif"
        aria-label="Configuration effective"
        aside={<span>ce que le moteur exécute</span>}
      >
        <div className="flex flex-col gap-1 text-sm">
          <Group first title="Two-trail" testId="settings-twotrail">
            <Row label="SL cliquet" value={s.twoTrail.slPoints} unit="pts" driftKey="slPoints" />
            <Row label="Renfort r" value={s.twoTrail.reinforceThresholdPoints} unit="pts" driftKey="reinforceThresholdPoints" />
            <Row label="Trail r_trail" value={s.twoTrail.trailDistancePoints} unit="pts" driftKey="trailDistancePoints" />
            <Row label="Max côté biais" value={s.twoTrail.maxSide} driftKey="maxSide" />
          </Group>
          <Group title="Caps & flux">
            <Row label="Cap dur (total)" value={s.positionLimits.maxTotalHard} driftKey="maxTotalHard" />
            <Row label="Stops consécutifs" value={s.positionLimits.consecutiveStopLimit} driftKey="consecutiveStopLimit" />
            <Row label="Spread max" value={s.stream.maxSpreadPoints} unit="pts" driftKey="maxSpreadPoints" />
          </Group>
          <Group title="Taille">
            <Row label="Valeur du point" value={s.sizing.pointValueEur} unit="€/pt" />
            <Row label="Taille max" value={s.sizing.positionSizeMaxEurPerPoint} unit="€/pt" />
          </Group>
          <Group title="Risque">
            <Row label="Risque cycle" value={pct(s.liveSafety.cycleRiskPct)} driftKey="cycleRiskPct" driftValue={s.liveSafety.cycleRiskPct} />
          </Group>
          {s.policy && (
            <Group title="Politique">
              <Row
                label="Flat marché fermé"
                value={
                  <span className={s.policy.flatWhenClosed ? "text-buy" : "text-warning"}>
                    {s.policy.flatWhenClosed ? "ON" : "OFF"}
                  </span>
                }
                driftKey="flatWhenClosed"
                driftValue={s.policy.flatWhenClosed}
              />
              <Row
                label="Avance du flatten"
                value={`T−${s.policy.flattenLeadMinutes}`}
                unit="min"
                driftKey="flattenLeadMinutes"
                driftValue={s.policy.flattenLeadMinutes}
              />
              <Row
                label="Financement"
                value={
                  <span className={s.policy.financingEnabled ? "text-buy" : "text-muted-foreground"}>
                    {s.policy.financingEnabled ? "ON" : "OFF"}
                  </span>
                }
                unit={`€STR ${(s.policy.financingBaseRatePct * 100).toFixed(2)}%`}
                driftKey="financingEnabled"
                driftValue={s.policy.financingEnabled}
              />
            </Group>
          )}
        </div>
      </Panel>
    </div>
  );
}

function Group({
  title,
  children,
  first = false,
  testId,
}: {
  title: string;
  children: ReactNode;
  first?: boolean;
  testId?: string;
}) {
  return (
    <div data-testid={testId} className={cn(!first && "pt-2")}>
      <div className="text-dim flex items-center gap-2 pb-1 text-[10px] font-bold tracking-widest uppercase">
        <span className="text-muted-foreground/70">{title}</span>
        <span className="bg-border/70 h-px flex-1" aria-hidden />
      </div>
      <dl className="m-0 flex flex-col">{children}</dl>
    </div>
  );
}

/**
 * One dense reference row: label · dotted leader · mono value (+unit) and, when the effective
 * value differs from the compiled default, an amber "≠ default" chip.
 */
function Row({
  label,
  value,
  unit,
  driftKey,
  driftValue,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  /** Key into COMPILED_DEFAULTS to check for drift (omit = never marked). */
  driftKey?: keyof typeof COMPILED_DEFAULTS;
  /** Raw value for the drift comparison when `value` is pre-formatted. */
  driftValue?: number | boolean;
}) {
  let drifted = false;
  let compiled: number | boolean | undefined;
  if (driftKey) {
    compiled = compiledOf(driftKey);
    const raw = driftValue !== undefined ? driftValue : (value as number);
    drifted =
      typeof compiled === "number" && typeof raw === "number"
        ? Math.abs(raw - compiled) > 1e-9
        : raw !== compiled;
  }
  return (
    <div className="border-border/40 flex items-baseline gap-2 border-b py-[3px] text-[13px] last:border-b-0">
      <dt className="text-muted-foreground m-0">{label}</dt>
      <span className="border-dim/30 flex-1 translate-y-[-3px] border-b border-dotted" aria-hidden />
      <dd className="text-foreground m-0 font-mono font-semibold tabular-nums">{value}</dd>
      {unit && <span className="text-dim text-[10.5px]">{unit}</span>}
      {drifted && (
        <span
          className="text-warning border-warning/45 bg-warning/10 rounded border px-1 text-[9px] leading-relaxed font-bold"
          data-testid={`drift-${driftKey}`}
          title={`diffère du défaut compilé (${String(compiled)})`}
        >
          ≠ {typeof compiled === "boolean" ? (compiled ? "ON" : "OFF") : String(compiled)}
        </span>
      )}
    </div>
  );
}
